// client/src/features/compliance-calendar/month.ts — the grid a month view is drawn on ·
// MAINT-009.
//
// `features/equipment/dates.ts` already owns DateOnly arithmetic (`addDays`, `daysBetween`,
// `monthOf`) and is imported rather than copied: the rule it states — three integers in, three
// integers out, `Date.UTC` so a day is always 86 400 000 ms and never 82 800 000 across a DST
// boundary — is the rule a calendar needs most, because the last Sunday of March is exactly the
// kind of cell a locally-computed grid loses.
//
// What is here is what a month VIEW needs and a countdown does not: the first and last day of a
// month, the cells that pad it out to whole weeks, and stepping one month at a time.
//
// THE WEEK STARTS ON MONDAY. `Date.getUTCDay()` counts from Sunday, which is an American
// convention; Romania's week starts luni, and the seven column headers of the grid are
// `insurance.calendar.weekdays.*` in that order. `weekdayIndex` does the rotation once so no
// caller has to remember the `+ 6 % 7`.

import { addDays, monthOf } from '@/features/equipment/dates';

/** `YYYY-MM`, the key a calendar groups and navigates on. */
export type CalendarMonth = string;

const MONTH_PATTERN = /^(\d{4})-(\d{2})$/;

/** Whether a string is a `YYYY-MM` this module can work with. */
export function isCalendarMonth(value: string): boolean {
  const match = MONTH_PATTERN.exec(value);
  if (match === null) return false;
  const month = Number(match[2]);
  return month >= 1 && month <= 12;
}

/** The month a DateOnly falls in — `monthOf`, re-exported so callers need one import. */
export function monthOfDate(dateOnly: string): CalendarMonth | null {
  return monthOf(dateOnly);
}

/** `"2026-09"` → `"2026-09-01"`. Null when the argument is not a `YYYY-MM`. */
export function firstDayOf(month: CalendarMonth): string | null {
  return isCalendarMonth(month) ? `${month}-01` : null;
}

/**
 * `"2026-09"` → `"2026-09-30"`.
 *
 * The first of the NEXT month minus one day, which is the only form that needs no table of
 * month lengths and no leap-year rule: February 2028 comes out 29 because `Date.UTC` says so.
 */
export function lastDayOf(month: CalendarMonth): string | null {
  const first = firstDayOf(month);
  if (first === null) return null;
  const next = shiftMonth(month, 1);
  const nextFirst = next === null ? null : firstDayOf(next);
  return nextFirst === null ? null : addDays(nextFirst, -1);
}

/** `shiftMonth("2026-12", 1)` → `"2027-01"`; `shiftMonth("2026-01", -1)` → `"2025-12"`. */
export function shiftMonth(month: CalendarMonth, delta: number): CalendarMonth | null {
  const match = MONTH_PATTERN.exec(month);
  if (match === null) return null;

  const year = Number(match[1]);
  const ordinal = Number(match[2]) - 1 + delta;
  const shiftedYear = year + Math.floor(ordinal / 12);
  const shiftedMonth = ((ordinal % 12) + 12) % 12;

  return `${String(shiftedYear).padStart(4, '0')}-${String(shiftedMonth + 1).padStart(2, '0')}`;
}

/**
 * Monday = 0 … Sunday = 6, for a DateOnly.
 *
 * `Date.getUTCDay()` counts Sunday = 0; Romania's week starts luni and the grid's first column
 * is luni, so the rotation happens once, here.
 */
export function weekdayIndex(dateOnly: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOnly);
  if (match === null) return null;

  const day = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
  ).getUTCDay();

  return (day + 6) % 7;
}

/** Every day of a month, in order. Empty when the argument is not a `YYYY-MM`. */
export function daysOf(month: CalendarMonth): string[] {
  const first = firstDayOf(month);
  const last = lastDayOf(month);
  if (first === null || last === null) return [];

  const days: string[] = [];
  for (let cursor: string | null = first; cursor !== null && cursor <= last; cursor = addDays(cursor, 1)) {
    days.push(cursor);
  }
  return days;
}

/** One cell of the grid: a day, and whether it belongs to the month being shown. */
export interface CalendarCell {
  date: string;
  inMonth: boolean;
}

/**
 * The month laid out as whole weeks, Monday-first — the cells a `grid-cols-7` renders.
 *
 * The leading and trailing cells are the neighbouring months' real days rather than blanks:
 * a policy that lapses on 1 October is worth seeing from September's grid, and a blank cell
 * would have nowhere to put it. `inMonth` is what dims them.
 */
export function monthGrid(month: CalendarMonth): CalendarCell[] {
  const days = daysOf(month);
  const first = days[0];
  const last = days.at(-1);
  if (first === undefined || last === undefined) return [];

  const leading = weekdayIndex(first) ?? 0;
  const trailing = 6 - (weekdayIndex(last) ?? 6);

  const cells: CalendarCell[] = [];

  for (let offset = leading; offset > 0; offset -= 1) {
    const date = addDays(first, -offset);
    if (date !== null) cells.push({ date, inMonth: false });
  }

  for (const date of days) cells.push({ date, inMonth: true });

  for (let offset = 1; offset <= trailing; offset += 1) {
    const date = addDays(last, offset);
    if (date !== null) cells.push({ date, inMonth: false });
  }

  return cells;
}
